import React from 'react'
import { Box, Button, IconButton, Link, Paper } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { CSVLink } from 'react-csv'
import { Table } from 'react-bootstrap'
import SettlementDetailContextHeader from '../../components/SettlementDetailContextHeader'
import {
  SETTLEMENT_REFUND_PRODUCTS_DOWNLOAD_LABEL,
  SETTLEMENT_REFUND_PRODUCTS_EMPTY_STATE,
  SETTLEMENT_REFUND_PRODUCTS_INVOICE_LINK_LABEL,
  SETTLEMENT_REFUND_PRODUCTS_MISSING_VALUE,
  SETTLEMENT_REFUND_PRODUCTS_TABLE_HEADERS,
  SETTLEMENT_REFUND_PRODUCTS_TITLE
} from '../../constants/settlementDetailsConstants'
import {
  UI_COLOR_ACCENT_ORANGE_ALT,
  UI_COLOR_ACCENT_ORANGE_BRIGHT,
  UI_COLOR_BORDER_CARD,
  UI_COLOR_SURFACE_MUTED,
  UI_COLOR_TEXT_BRAND_DARK,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import GoogleIcon from '../../icon/GoogleIcon'
import { formatMoney } from '../../helpers/utils'

export default function SettlementRefundProductsTab({
  settlement = null,
  refunds = [],
  csvData,
  filename,
  actions = null
}) {
  const downloadButton =
    refunds.length > 0 ? (
      <Button
        component={CSVLink}
        data={csvData}
        filename={filename}
        variant="contained"
        startIcon={<DownloadIcon />}
        sx={{
          textDecoration: 'none',
          backgroundColor: UI_COLOR_ACCENT_ORANGE_BRIGHT,
          color: `${UI_COLOR_WHITE} !important`,
          '& .MuiButton-startIcon': {
            color: `${UI_COLOR_WHITE} !important`
          },
          '&:hover': {
            backgroundColor: UI_COLOR_ACCENT_ORANGE_ALT
          }
        }}
      >
        {SETTLEMENT_REFUND_PRODUCTS_DOWNLOAD_LABEL}
      </Button>
    ) : (
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        disabled
        sx={{
          backgroundColor: UI_COLOR_ACCENT_ORANGE_BRIGHT,
          color: `${UI_COLOR_WHITE} !important`,
          '& .MuiButton-startIcon': {
            color: `${UI_COLOR_WHITE} !important`
          }
        }}
      >
        {SETTLEMENT_REFUND_PRODUCTS_DOWNLOAD_LABEL}
      </Button>
    )

  return (
    <Box>
      <SettlementDetailContextHeader
        title={SETTLEMENT_REFUND_PRODUCTS_TITLE}
        settlement={settlement}
        actions={actions || downloadButton}
      />

      <Paper elevation={0} sx={{ overflow: 'hidden' }}>
        <Table hover responsive>
          <thead className="table-header-gray-mui">
            <tr>
              {SETTLEMENT_REFUND_PRODUCTS_TABLE_HEADERS.map((header) => (
                <th
                  key={header}
                  className={
                    header === 'PRECIO' || header === 'TOTAL'
                      ? 'td-number-align-right'
                      : undefined
                  }
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {refunds.length > 0 ? (
              refunds.map((refund, index) => (
                <RefundRows key={refund.uuid || index} refund={refund} />
              ))
            ) : (
              <tr>
                <td colSpan="9">{SETTLEMENT_REFUND_PRODUCTS_EMPTY_STATE}</td>
              </tr>
            )}
          </tbody>
        </Table>
      </Paper>
    </Box>
  )
}

function RefundRows({ refund }) {
  const [open, setOpen] = React.useState(true)
  const details = refund.refund_details || []

  return (
    <>
      <tr>
        <td
          colSpan="9"
          className="td-text-bold"
          style={{
            backgroundColor: UI_COLOR_SURFACE_MUTED,
            borderBottom: `1px solid ${UI_COLOR_BORDER_CARD}`,
            color: UI_COLOR_TEXT_BRAND_DARK,
            padding: '14px 16px'
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              onClick={() => setOpen((prev) => !prev)}
              sx={{ color: UI_COLOR_TEXT_BRAND_DARK, p: 0.25 }}
            >
              {open ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </IconButton>
            <Box component="span" sx={{ flex: 1, fontSize: 14, fontWeight: 800 }}>
              {`${refund?.invoice_number} | ${refund?.client_db_ref} - ${refund?.client_description}`}
            </Box>
            <Link
              component="button"
              onClick={() => window.open(`/devoluciones/${refund.uuid}`, '_blank')}
              sx={{ fontSize: 14, fontWeight: 700 }}
            >
              {SETTLEMENT_REFUND_PRODUCTS_INVOICE_LINK_LABEL}
            </Link>
          </Box>
        </td>
      </tr>
      {open &&
        details.map((detail, detailIndex) => (
          <tr key={`${refund.uuid || refund.invoice_number}-${detailIndex}`}>
            <DetailCell>
              <Box display="flex" alignItems="center" justifyContent="center">
                <GoogleIcon name="subdirectory_arrow_right" size={18} color={UI_COLOR_TEXT_BRAND_DARK} />
              </Box>
            </DetailCell>
            <DetailCell>{detail?.product_db_ref}</DetailCell>
            <DetailCell>{detail?.product_barcode || SETTLEMENT_REFUND_PRODUCTS_MISSING_VALUE}</DetailCell>
            <DetailCell>{detail?.product_description || SETTLEMENT_REFUND_PRODUCTS_MISSING_VALUE}</DetailCell>
            <DetailCell>
              {detail?.motive?.replaceAll('  ', '') || refund.motive?.replaceAll('  ', '')}
            </DetailCell>
            <DetailCell>{detail?.unit_of_sale}</DetailCell>
            <DetailCell>{Number(detail?.quantity || 0)}</DetailCell>
            <DetailCell align="right">{formatMoney(Number(detail?.unit_price || 0))}</DetailCell>
            <DetailCell align="right">{formatMoney(Number(detail?.extended_price || 0))}</DetailCell>
          </tr>
        ))}
    </>
  )
}

function DetailCell({ children, align = 'left' }) {
  return (
    <td
      className="td-text-limited"
      align={align}
      style={{
        color: UI_COLOR_TEXT_BRAND_DARK,
        fontSize: 14,
        paddingTop: '12px',
        paddingBottom: '12px',
        borderBottom: `1px solid ${UI_COLOR_BORDER_CARD}`
      }}
    >
      {children}
    </td>
  )
}
